import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { generatePath, useNavigate } from "react-router-dom";
import classNames from "classnames";
import {
  AppInput,
  AppButton,
  AppSingleSelect,
  AppConfirmDialog,
} from "@pro-sto/ui";
import type { SelectOption } from "@pro-sto/ui";
import {
  authStore,
  employeesStore,
  employeePositionsStore,
  paymentSchemeTemplatesStore,
  toastStore,
} from "@/stores";
import { ROUTES } from "@/constants/routes";
import { adminEmployeePaymentSchemeApplyTemplate } from "@/services/employeesApi";
import { AppError } from "@/utils/errors";
import type { EmployeeListItem } from "@/types/employee.types";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ExclamationMarkIcon,
} from "@/components/icons";
import { TextTooltip } from "@/components/TextTooltip";
import { formatPhoneWithMask } from "@/features";
import "./EmployeesPage.css";

const SUB_TABS = [
  { id: "shifts", label: "Смены" },
  { id: "employees", label: "Сотрудники" },
  { id: "payroll", label: "Начисления" },
] as const;

type SubTabId = (typeof SUB_TABS)[number]["id"];

const NO_TEMPLATE_OPTION: SelectOption = { label: "Без шаблона", value: null };

const initials = (firstName: string, lastName: string) =>
  `${lastName?.[0] ?? ""}${firstName?.[0] ?? ""}`.toUpperCase();

const EmployeesPage: React.FC = observer(() => {
  const navigate = useNavigate();
  const serviceCenterUuid = authStore.user?.service_center_uuid;

  const [activeSubTab, setActiveSubTab] = useState<SubTabId>("employees");
  const [employeeToDelete, setEmployeeToDelete] =
    useState<EmployeeListItem | null>(null);
  const [isDeletingEmployee, setIsDeletingEmployee] = useState(false);
  const [applyingTemplateUuid, setApplyingTemplateUuid] = useState<
    string | null
  >(null);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!serviceCenterUuid) return;
    employeesStore.setServiceCenterUuid(serviceCenterUuid);
    employeePositionsStore.loadPositions(serviceCenterUuid);
    paymentSchemeTemplatesStore.setServiceCenterUuid(serviceCenterUuid);
  }, [serviceCenterUuid]);

  // Бесконечная подгрузка: как только сентинел внизу списка попадает во
  // вьюпорт скроллящегося контейнера — подгружаем следующую порцию.
  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && employeesStore.hasMore && !employeesStore.isLoadingMore) {
          employeesStore.loadMore();
        }
      },
      { root, rootMargin: "200px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [employeesStore.hasMore]);

  const templateOptions: SelectOption[] = [
    NO_TEMPLATE_OPTION,
    ...paymentSchemeTemplatesStore.activeOptions,
  ];

  const handleApplyTemplate = async (
    employee: EmployeeListItem,
    option: SelectOption | null,
  ) => {
    const templateUuid = (option?.value as string | null) ?? null;
    if (templateUuid === employee.template.uuid) return;

    setApplyingTemplateUuid(employee.uuid);
    try {
      // keep_overrides: true (дефолт по контракту) — ручные ставки сотрудника
      // сохраняются, шаблон подставляется только туда, где их не было.
      await adminEmployeePaymentSchemeApplyTemplate(
        employee.uuid,
        { template_uuid: templateUuid, keep_overrides: true },
        false,
      );
      toastStore.showSuccess(
        templateUuid
          ? "Шаблон применён"
          : "Сотрудник переведён на ручные ставки",
      );
      await employeesStore.fetchList();
    } catch (error) {
      if (error instanceof AppError && (error.statusCode === 404 || error.statusCode === 409)) {
        toastStore.showError(error.message);
      } else if (error instanceof AppError && error.statusCode === 400) {
        toastStore.showError(
          "Не удалось применить шаблон: некорректные данные",
        );
      } else {
        toastStore.showError("Не удалось применить шаблон");
      }
    } finally {
      setApplyingTemplateUuid(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!employeeToDelete) return;
    setIsDeletingEmployee(true);
    const success = await employeesStore.deleteEmployee(employeeToDelete.uuid);
    setIsDeletingEmployee(false);
    if (success) setEmployeeToDelete(null);
  };

  return (
    <div className="employees-page" data-testid="employees-page">
      <div className="employees-page__container">
        <div className="employees-page__toolbar">
          <div className="employees-page__search">
            <AppInput
              placeholder="Имя, номер"
              value={employeesStore.filters.search}
              onChange={(value) => employeesStore.setSearch(value)}
              data-testid="employees-search-input"
            />
          </div>

          <div className="employees-page__subtabs" role="tablist">
            {SUB_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeSubTab === tab.id}
                className={classNames("employees-page__subtab", {
                  "employees-page__subtab_active": activeSubTab === tab.id,
                })}
                onClick={() => setActiveSubTab(tab.id)}
                data-testid={`employees-subtab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <AppButton
            variant="primary"
            iconRight={<PlusIcon size={16} />}
            onClick={() => navigate(ROUTES.EMPLOYEE_NEW)}
            data-testid="employees-add-button"
          >
            Добавить сотрудника
          </AppButton>
        </div>

        {activeSubTab !== "employees" ? (
          <div
            className="employees-page__state"
            data-testid="employees-subtab-stub"
          >
            Раздел «{SUB_TABS.find((tab) => tab.id === activeSubTab)?.label}»
            в разработке
          </div>
        ) : (
        <div className="employees-page__table" data-testid="employees-table">
          <div className="employees-page__table-header">
            <div className="employees-page__col employees-page__col_employee">
              Сотрудник
            </div>
            <div className="employees-page__col employees-page__col_role">
              Должность
            </div>
            <div className="employees-page__col employees-page__col_phone">
              Телефон
            </div>
            <div className="employees-page__col employees-page__col_email">
              Почта
            </div>
            <div className="employees-page__col employees-page__col_rate">
              Схема оплаты
            </div>
          </div>

          {employeesStore.isLoading && employeesStore.items.length === 0 && (
            <div
              className="employees-page__skeleton"
              data-testid="employees-loading-skeleton"
            >
              {Array.from({ length: 6 }).map((_, index) => (
                <div className="employees-page__skeleton-row" key={index}>
                  <span className="employees-page__skeleton-block employees-page__skeleton-block_avatar" />
                  <span className="employees-page__skeleton-block employees-page__skeleton-block_name" />
                  <span className="employees-page__skeleton-block employees-page__skeleton-block_role" />
                  <span className="employees-page__skeleton-block employees-page__skeleton-block_phone" />
                  <span className="employees-page__skeleton-block employees-page__skeleton-block_email" />
                  <span className="employees-page__skeleton-block employees-page__skeleton-block_rate" />
                </div>
              ))}
            </div>
          )}

          {!employeesStore.isLoading &&
            employeesStore.loadError &&
            employeesStore.items.length === 0 && (
              <div
                className="employees-page__error-state"
                data-testid="employees-error-state"
              >
                <ExclamationMarkIcon size={24} />
                <p className="employees-page__error-message">
                  {employeesStore.loadError}
                </p>
                <AppButton
                  variant="secondary"
                  onClick={() => employeesStore.fetchList()}
                  data-testid="employees-error-retry"
                >
                  Повторить
                </AppButton>
              </div>
            )}

          {!employeesStore.isLoading &&
            !employeesStore.loadError &&
            employeesStore.items.length === 0 && (
              <div
                className="employees-page__state"
                data-testid="employees-empty-state"
              >
                Сотрудники не найдены
              </div>
            )}

          <div
            className="employees-page__rows"
            ref={scrollRef}
            data-testid="employees-rows-scroll"
          >
          {employeesStore.items.map((employee) => (
            <div
              className="employees-page__row"
              key={employee.uuid}
              data-testid={`employee-row-${employee.uuid}`}
            >
              <div className="employees-page__col employees-page__col_employee">
                <span className="employees-page__avatar">
                  {initials(employee.first_name, employee.last_name)}
                </span>
                <TextTooltip
                  content={`${employee.last_name} ${employee.first_name}`}
                  className="employees-page__name"
                >
                  <span className="employees-page__name-part">
                    {employee.last_name}
                  </span>
                  <span className="employees-page__name-part">
                    {employee.first_name}
                  </span>
                </TextTooltip>
              </div>
              <div className="employees-page__col employees-page__col_role">
                <TextTooltip
                  content={employee.position.name}
                  className="employees-page__role"
                >
                  <span className="employees-page__role-text">
                    {employee.position.name}
                  </span>
                </TextTooltip>
              </div>
              <div className="employees-page__col employees-page__col_phone">
                <TextTooltip
                  content={formatPhoneWithMask(employee.phone)}
                  className="employees-page__phone"
                >
                  <span className="employees-page__phone-text">
                    {formatPhoneWithMask(employee.phone)}
                  </span>
                </TextTooltip>
              </div>
              <div className="employees-page__col employees-page__col_email">
                {employee.email ? (
                  <TextTooltip
                    content={employee.email}
                    className="employees-page__email"
                  >
                    <span className="employees-page__email-text">
                      {employee.email}
                    </span>
                  </TextTooltip>
                ) : (
                  "—"
                )}
              </div>
              <div className="employees-page__col employees-page__col_rate">
                <div className="employees-page__rate-select">
                  <AppSingleSelect
                    options={templateOptions}
                    value={
                      employee.template.uuid
                        ? {
                            label:
                              templateOptions.find(
                                (option) =>
                                  option.value === employee.template.uuid,
                              )?.label ?? employee.template.name ?? "",
                            value: employee.template.uuid,
                          }
                        : NO_TEMPLATE_OPTION
                    }
                    onChange={(option) => handleApplyTemplate(employee, option)}
                    disabled={applyingTemplateUuid === employee.uuid}
                    background="muted"
                    data-testid={`employee-template-select-${employee.uuid}`}
                  />
                </div>
                <div className="employees-page__row-actions">
                  {employee.template.has_overrides && (
                    <span
                      className="employees-page__warning-badge"
                      title="Ставки отличаются от шаблона"
                      data-testid={`employee-overrides-badge-${employee.uuid}`}
                    >
                      <ExclamationMarkIcon size={16} />
                    </span>
                  )}
                  <button
                    type="button"
                    className="employees-page__icon-button"
                    onClick={() =>
                      navigate(
                        generatePath(ROUTES.EMPLOYEE_EDIT, {
                          uuid: employee.uuid,
                        }),
                      )
                    }
                    title="Редактировать"
                    data-testid={`employee-edit-button-${employee.uuid}`}
                  >
                    <PencilIcon size={16} />
                  </button>
                  <button
                    type="button"
                    className="employees-page__icon-button"
                    onClick={() => setEmployeeToDelete(employee)}
                    title="Удалить"
                    data-testid={`employee-delete-button-${employee.uuid}`}
                  >
                    <TrashIcon size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}

            {employeesStore.hasMore && (
              <div
                ref={sentinelRef}
                className="employees-page__sentinel"
                data-testid="employees-load-more-sentinel"
              >
                {employeesStore.isLoadingMore && (
                  <span className="employees-page__state">Загрузка...</span>
                )}
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      <AppConfirmDialog
        isOpen={!!employeeToDelete}
        title="Удалить сотрудника?"
        message={`Вы действительно хотите удалить сотрудника «${
          employeeToDelete
            ? `${employeeToDelete.last_name} ${employeeToDelete.first_name}`
            : ""
        }»? Это действие нельзя будет отменить.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setEmployeeToDelete(null)}
        isConfirming={isDeletingEmployee}
        confirmingText="Удаление..."
        data-testid="confirm-delete-employee-modal"
      />
    </div>
  );
});

EmployeesPage.displayName = "EmployeesPage";

export default EmployeesPage;
